import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { NativePurchases } from '@capgo/native-purchases';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, CreditCard, RefreshCw, Zap, Shield, Share2, Check, X, Star, Crown, Building, User, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation, useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from '../../App';

// Stripe initialization (unchanged)
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    console.log('🔑 [MOBILE] Stripe Publishable Key:', publishableKey ? 'Set' : 'Missing');

    if (!publishableKey) {
      console.error('❌ [MOBILE] Stripe publishable key is missing!');
      throw new Error('Stripe configuration error: Missing publishable key');
    }

    if (!publishableKey.startsWith('pk_')) {
      console.error('❌ [MOBILE] Invalid Stripe publishable key format');
      throw new Error('Stripe configuration error: Invalid key format');
    }

    stripePromise = new Promise((resolve, reject) => {
      if (window.Stripe) {
        console.log('✅ [MOBILE] Stripe already loaded, initializing...');
        resolve(window.Stripe(publishableKey));
        return;
      }

      console.log('📥 [MOBILE] Loading Stripe.js from CDN...');
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        console.log('✅ [MOBILE] Stripe.js loaded successfully');
        if (window.Stripe) {
          resolve(window.Stripe(publishableKey));
        } else {
          reject(new Error('Stripe.js failed to load properly'));
        }
      };
      script.onerror = () => {
        console.error('❌ [MOBILE] Failed to load Stripe.js');
        reject(new Error('Failed to load Stripe.js'));
      };
      document.head.appendChild(script);
    });
  }
  return stripePromise;
};

const MobileUpgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language: contextLanguage, isLoading: languageLoading, isInitialized } = useLanguage();
  const { user, httpClient, validateToken } = useAuth();

  const [language, setLanguage] = useState('en');
  const [unreadCount, setUnreadCount] = useState(0);
  const [userType, setUserType] = useState('individual');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState({ free: false, premium: false });
  const [billingInterval, setBillingInterval] = useState('monthly');

  // IAP specific state
  const [iosProducts, setIosProducts] = useState([]);
  const [processingIAP, setProcessingIAP] = useState(false);

  const getPlatform = () => Capacitor.getPlatform();
  const isIOS = getPlatform() === 'ios';

  // ---------- Language Initialization ----------
  useEffect(() => {
    const savedLanguage = localStorage.getItem('easysubs-language');
    console.log('🌐 [LANGUAGE] Initial language from localStorage:', savedLanguage);
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'it')) {
      setLanguage(savedLanguage);
    } else {
      setLanguage('en');
      localStorage.setItem('easysubs-language', 'en');
    }
  }, []);

  useEffect(() => {
    if (contextLanguage && (contextLanguage === 'en' || contextLanguage === 'it')) {
      console.log('🔄 [LANGUAGE] Syncing with context language:', contextLanguage);
      setLanguage(contextLanguage);
    }
  }, [contextLanguage]);

  // ---------- Data Fetching ----------
  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;
      const response = await httpClient.request('/api/notifications');
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchUserData = async () => {
    try {
      if (!httpClient || !user) {
        setLoading(false);
        return;
      }
      const response = await httpClient.request('/api/user/account');
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchUserData();
        }
        throw new Error('Failed to fetch user data');
      }
      const userData = await response.json();
      console.log('👤 [USER] User data fetched:', userData);
      setUserData(userData);
      setUserType(userData.userType || 'individual');
    } catch (err) {
      console.error('❌ Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    console.log('🌐 [PLANS] Fetching plans with language:', language, 'and userType:', userType);
    try {
      if (!httpClient) return;
      setPlansLoading(true);
      const url = `/api/plans/${userType}?language=${language}`;
      console.log('📡 [PLANS] API URL:', url);
      const response = await httpClient.request(url);
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchPlans();
        }
        throw new Error(`Failed to fetch plans: ${response.status}`);
      }
      const plansData = await response.json();
      console.log('📦 [PLANS] Raw plans from API:', JSON.stringify(plansData, null, 2));
      setPlans(plansData);
      if (plansData.length === 0) {
        console.log('⚠️ [PLANS] No plans found, trying fallback...');
        await fetchPlansFallback();
      }
    } catch (err) {
      console.error('❌ [PLANS] Error fetching plans:', err);
      await fetchPlansFallback();
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchPlansFallback = async () => {
    try {
      if (!httpClient) return;
      console.log('🔄 [PLANS] Fallback: fetching all plans without language filter...');
      const response = await httpClient.request(`/api/plans/${userType}`);
      if (!response.ok) throw new Error('Fallback also failed');
      const fallbackPlans = await response.json();
      console.log('📦 [PLANS] Fallback plans received:', fallbackPlans);
      setPlans(fallbackPlans);
    } catch (fallbackErr) {
      console.error('❌ [PLANS] Fallback failed:', fallbackErr);
      setPlans([]);
      setUpgradeError('Failed to load plans. Please try again later.');
    }
  };

  // ---------- IAP Initialization ----------
  useEffect(() => {
    if (isIOS) {
      console.log('🍏 [IAP] Platform is iOS, initializing IAP...');
      initializeIAP();
    } else {
      console.log('🌍 [IAP] Platform is', getPlatform(), '- skipping IAP initialization');
    }
  }, [isIOS]);

  const initializeIAP = async () => {
    try {
      await NativePurchases.initialize();
      console.log('✅ [IAP] NativePurchases initialized successfully');
    } catch (err) {
      // initialize() throws if already initialized — this is expected and safe to ignore
      console.warn('⚠️ [IAP] NativePurchases.initialize() warning (may already be initialized):', err.message);
    }
  };

  // Fetch iOS products after plans are loaded
  useEffect(() => {
    if (isIOS && plans.length > 0) {
      console.log('🍏 [IAP] Plans are available, attempting to fetch iOS products...');
      fetchIosProducts();
    }
  }, [isIOS, plans]);

  const fetchIosProducts = async () => {
    try {
      console.log('🍏 [IAP] Starting fetchIosProducts...');
      // Extract all Apple product IDs from plans
      const productIds = plans.flatMap(plan => {
        const ids = [];
        if (plan.apple?.monthlyProductId) {
          console.log(`🍏 [IAP] Found monthly product ID for plan ${plan.planId}: ${plan.apple.monthlyProductId}`);
          ids.push(plan.apple.monthlyProductId);
        }
        if (plan.apple?.yearlyProductId) {
          console.log(`🍏 [IAP] Found yearly product ID for plan ${plan.planId}: ${plan.apple.yearlyProductId}`);
          ids.push(plan.apple.yearlyProductId);
        }
        if (!plan.apple) {
          console.warn(`⚠️ [IAP] Plan ${plan.planId} has no 'apple' field`);
        }
        return ids;
      }).filter(Boolean);

      console.log('📦 [IAP] Extracted product IDs array:', productIds);

      if (productIds.length === 0) {
        console.log('⚠️ [IAP] No Apple product IDs found in plans. Check the plan documents for the "apple" field.');
        return;
      }

      console.log('📦 [IAP] Requesting products from App Store with IDs:', productIds);
      const products = await NativePurchases.getProducts({ 
        productIdentifiers: productIds,
        productType: 'subs'
      });
      console.log('✅ [IAP] Raw response from NativePurchases.getProducts:', products);

      // The plugin returns an object with a "products" array
      const productsArray = products.products || [];
      console.log('✅ [IAP] Fetched products array:', productsArray);
      setIosProducts(productsArray);
    } catch (err) {
      console.error('❌ [IAP] Fetch products error:', err);
      setUpgradeError('Failed to fetch in-app purchase products.');
    }
  };

  // IAP purchase (restore removed – handled automatically)
  const handleIosPurchase = async (plan) => {
    if (!httpClient || !user) {
      console.error('❌ [IAP] No httpClient or user');
      return;
    }

    // product ID based on billing interval
    const productId = billingInterval === 'yearly'
      ? plan.apple?.yearlyProductId
      : plan.apple?.monthlyProductId;
    if (!productId) {
      setUpgradeError('No Apple product ID for this plan.');
      console.error('❌ [IAP] No productId for plan:', plan.planId);
      return;
    }

    console.log('🍏 [IAP] Starting purchase for product:', productId);

    // Account type check
    if (userData.userType === 'individual' && userType === 'company') {
      setUpgradeError(t('upgrade.personalToBusinessError') || 'Personal accounts cannot upgrade to company plans.');
      return;
    }
    if (userData.userType === 'company' && userType === 'individual') {
      setUpgradeError(t('upgrade.businessToPersonalError') || 'Business accounts cannot upgrade to individual plans.');
      return;
    }

    setProcessingIAP(true);
    setUpgradeError('');
    setUpgradeSuccess('');

    try {
      const product = iosProducts.find(p => p.identifier === productId);
      if (!product) {
        console.error('❌ [IAP] Product not found in iosProducts:', productId);
        throw new Error('Product not found in App Store');
      }

      const purchaseResult = await NativePurchases.purchaseProduct({ productIdentifier: productId });
      console.log('✅ [IAP] Purchase result:', purchaseResult);
      const transaction = purchaseResult.transaction; // property may vary

      // Replace getReceipt() with jwsRepresentation
      const response = await httpClient.request('/api/ios/verify-receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jwsRepresentation: purchaseResult.jwsRepresentation,
          transactionId: purchaseResult.transactionId,
          productId: purchaseResult.productIdentifier
        })
      });

      // Handle stale_transaction error
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'stale_transaction') {
          // Stale transaction delivered by StoreKit cache after a cancellation
          // User needs to tap Upgrade once more to trigger a fresh payment sheet
          console.warn('⚠️ [IAP] Stale transaction detected, prompting user to try again');
          throw new Error('Your previous subscription has expired. Please tap Upgrade again to start a new subscription.');
        }
        throw new Error(errorData.error || 'Receipt verification failed');
      }

      const data = await response.json();
      console.log('✅ [IAP] Backend verification success:', data);
      setUpgradeSuccess('Subscription activated successfully!');
      fetchUserData();

      // Transaction auto-finished by @capgo/native-purchases on iOS
      console.log('✅ [IAP] Transaction auto-finished by plugin');
    } catch (error) {
      console.error('❌ [IAP] Purchase error:', error);
      setUpgradeError(error.message || 'Purchase failed. Please try again.');
    } finally {
      setProcessingIAP(false);
    }
  };

  // Stripe upgrade handler (unchanged but with platform guard)
  const handleUpgrade = async (plan) => {
    if (!httpClient || !user) {
      console.error('❌ [MOBILE] No user or httpClient available');
      return;
    }

    if (isIOS) {
      console.log('🍏 [STRIPE] Platform is iOS – should use IAP, not Stripe');
      return;
    }

    console.log('🔵 [MOBILE] Upgrade button clicked for plan:', plan.name);
    console.log('🌐 [MOBILE] Current language for upgrade:', language);

    // Check account type consistency
    if (userData.userType === 'individual' && userType === 'company') {
      setUpgradeError(t('upgrade.personalToBusinessError') || 'Personal accounts cannot upgrade to company plans. Please contact support to change your account type.');
      return;
    }
    if (userData.userType === 'company' && userType === 'individual') {
      setUpgradeError(t('upgrade.businessToPersonalError') || 'Business accounts cannot upgrade to individual plans. Please contact support to change your account type.');
      return;
    }

    setUpgradeError('');
    setUpgradeSuccess('');
    setProcessingPayment(true);

    try {
      // Get the Stripe price ID (monthly or yearly)
      const priceId = billingInterval === 'yearly'
        ? plan.stripe.yearlyPriceId
        : plan.stripe.monthlyPriceId;
      console.log('💰 [MOBILE] Stripe Price ID:', priceId);

      if (!priceId) {
        setUpgradeError(t('upgrade.planNotAvailable') || 'Selected plan is not available.');
        setProcessingPayment(false);
        return;
      }

      console.log('📤 [MOBILE] Creating checkout session...');

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/mobile-upgrade?success=true`;
      const cancelUrl = `${baseUrl}/mobile-upgrade?canceled=true`;

      console.log('📍 [MOBILE] Return URLs:', { successUrl, cancelUrl });

      const response = await httpClient.request('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: priceId,
          planId: plan.planId,
          language: language,
          successUrl: successUrl,
          cancelUrl: cancelUrl,
          isMobile: true
        })
      });

      console.log('📥 [MOBILE] Response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return handleUpgrade(plan);
        }

        const errorText = await response.text();
        console.error('❌ [MOBILE] API Error:', errorText);
        throw new Error('Failed to create checkout session');
      }

      const sessionData = await response.json();
      console.log('✅ [MOBILE] Session data received:', sessionData);

      const sessionId = sessionData.id;
      console.log('🎫 [MOBILE] Session ID:', sessionId);

      if (!sessionId) {
        throw new Error('No session ID returned from server');
      }

      // Initialize and redirect to Stripe Checkout
      console.log('🚀 [MOBILE] Initializing Stripe...');
      try {
        const stripe = await getStripe();
        console.log('✅ [MOBILE] Stripe initialized successfully');

        console.log('🔀 [MOBILE] Redirecting to Stripe Checkout...');
        const { error } = await stripe.redirectToCheckout({ sessionId });

        if (error) {
          console.error('❌ [MOBILE] Stripe checkout error:', error);
          setUpgradeError(`${t('upgrade.paymentError') || 'Payment error'}: ${error.message}`);
        } else {
          console.log('✅ [MOBILE] Redirect initiated successfully');
        }
      } catch (stripeError) {
        console.error('❌ [MOBILE] Stripe initialization failed:', stripeError);
        setUpgradeError(t('upgrade.paymentUnavailable') || 'Payment processing is temporarily unavailable. Please try again later.');
      }
    } catch (err) {
      console.error('💥 [MOBILE] Upgrade process failed:', err);
      setUpgradeError(err.message || t('upgrade.paymentUnavailable') || 'Payment processing is temporarily unavailable. Please try again later.');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Helper functions
  const isUpgradeAllowed = () => userData && userData.userType === userType;
  const getCurrentPlan = () => plans.find(p => p.tier === userData?.userPlan);
  const getPlanByTier = (tier) => plans.find(p => p.tier === tier);
  const toggleFeatures = (planType) => setExpandedFeatures(prev => ({ ...prev, [planType]: !prev[planType] }));
  const getFeaturesToShow = (plan, planType) => {
    if (!plan?.features) return [];
    return expandedFeatures[planType] ? plan.features : plan.features.slice(0, 5);
  };

  // Lifecycle effects
  useEffect(() => {
    if (!user) {
      navigate("/demo");
      return;
    }
    fetchUnreadNotifications();
    fetchUserData();
    const interval = setInterval(fetchUnreadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, httpClient]);

  // For iOS, force userType to 'individual' to hide business plans
  useEffect(() => {
    if (isIOS && userType === 'company') {
      setUserType('individual');
    }
  }, [isIOS, userType]);

  useEffect(() => {
    if (userType && language && httpClient) {
      console.log('🔄 [EFFECT] userType or language changed, fetching plans...');
      fetchPlans();
    } else {
      console.log('⏳ [EFFECT] Waiting for userType, language, or httpClient:', { userType, language, httpClient: !!httpClient });
    }
  }, [userType, language, httpClient]);

  // Handle Stripe redirects
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const currentPath = location.pathname;

    if (queryParams.get('success') || queryParams.get('canceled')) {
      console.log('🎯 [MOBILE] Processing Stripe redirect');

      if (currentPath === '/upgrade') {
        console.log('🔄 [MOBILE] Redirecting from desktop to mobile route');
        const newSearch = queryParams.toString();
        navigate(`/mobile-upgrade?${newSearch}`, { replace: true });
        return;
      }

      if (currentPath === '/mobile-upgrade') {
        if (queryParams.get('success')) {
          console.log('✅ [MOBILE] Payment success detected');
          setUpgradeSuccess(t('upgrade.paymentSuccess') || 'Payment successful! Your plan has been upgraded.');
          fetchUserData();
          navigate('/mobile-upgrade', { replace: true });
        } else if (queryParams.get('canceled')) {
          console.log('❌ [MOBILE] Payment canceled detected');
          setUpgradeError(t('upgrade.paymentCanceled') || 'Payment was canceled. Please try again.');
          navigate('/mobile-upgrade', { replace: true });
        }
      }
    }
  }, [location, navigate, t]);

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading') || 'Loading...'}</p>
          <p className="text-sm text-gray-500 mt-2">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (plansLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading') || 'Loading...'}</p>
          <p className="text-sm text-gray-500 mt-2">Loading plans... (Language: {language})</p>
        </div>
      </div>
    );
  }

  const freePlan = getPlanByTier('free');
  const premiumPlan = getPlanByTier('premium');
  const currentPlan = getCurrentPlan();

  const navItems = [
    { icon: Home, label: t('bottomNav.home') || "Home", path: "/mobile-dashboard" },
    {
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications') || "Notifications",
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace') || "Marketplace", path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs') || "Share Subs", path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank') || "Connect Bank", path: "/mobile-connectBank" },
    { icon: Users, label: t('bottomNav.partners') || "Partners", path: "/mobile-partners" },
    { icon: Settings, label: t('bottomNav.settings') || "Settings", path: "/mobile-account-settings" },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} />

      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-[#253985] to-amber-500 text-white p-4 rounded-lg">
            <h1 className="text-xl font-bold mb-2">{t('upgrade.title') || 'Upgrade Your Plan'}</h1>
            <p className="text-white/90 text-sm">
              {userData?.userPlan === 'premium'
                ? t('upgrade.currentPremium') || `You're currently on our Premium plan`
                : t('upgrade.upgradeFromFree') || `Upgrade from Free to Premium for more features`}
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="text-center space-y-4 mb-4">
              <Crown className="h-10 w-10 mx-auto text-yellow-500" />
              <h2 className="text-xl font-bold text-gray-800">{t('upgrade.choosePlan') || 'Choose Your Plan'}</h2>
              <p className="text-gray-600 text-sm">
                {userType === 'company'
                  ? t('upgrade.selectPlanDescriptionBusiness') || 'Select the plan that works best for your business subscription management needs'
                  : t('upgrade.selectPlanDescriptionPersonal') || 'Select the plan that works best for your personal subscription management needs'}
              </p>
            </div>

            {/* Current Plan Indicator */}
            {currentPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <Star className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800 text-sm">{t('upgrade.yourCurrentPlan') || 'Your Current Plan'}</h3>
                    <p className="text-blue-600 text-xs">{currentPlan.name}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {upgradeSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <p className="text-green-700 text-sm">{upgradeSuccess}</p>
                </div>
              </div>
            )}

            {/* User Type Toggle - Hidden on iOS */}
            {!isIOS && (
              <div className="flex justify-center mb-4">
                <div className="bg-gray-100 p-1 rounded-lg inline-flex w-full">
                  <button
                    className={`px-3 py-2 rounded-md flex items-center justify-center transition-colors flex-1 ${
                      userType === 'individual' ? 'bg-white text-[#253985] shadow-sm' : 'bg-transparent text-gray-600'
                    }`}
                    onClick={() => setUserType('individual')}
                  >
                    <User className="h-4 w-4 mr-1" />
                    <span className="text-sm">{t('upgrade.personal') || 'Personal'}</span>
                  </button>
                  <button
                    className={`px-3 py-2 rounded-md flex items-center justify-center transition-colors flex-1 ${
                      userType === 'company' ? 'bg-white text-[#253985] shadow-sm' : 'bg-transparent text-gray-600'
                    }`}
                    onClick={() => setUserType('company')}
                  >
                    <Building className="h-4 w-4 mr-1" />
                    <span className="text-sm">{t('upgrade.business') || 'Business'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {upgradeError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  <p className="text-red-700 text-sm">{upgradeError}</p>
                </div>
              </div>
            )}

            {/* No Plans Found */}
            {!freePlan && !premiumPlan && !plansLoading && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-yellow-500 mr-2" />
                  <div>
                    <p className="text-yellow-700 font-semibold text-sm">No plans found</p>
                    <p className="text-yellow-600 text-xs">
                      No plans available for {userType} type in {language} language.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Plans */}
            <div className="space-y-4 mb-4">
              {/* Free Plan */}
              {freePlan && (
                <div
                  className={`border rounded-xl p-4 transition-all ${
                    userData?.userPlan === 'free' ? 'border-[#253985] shadow-lg bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-1 rounded-lg">
                        <Star className="h-5 w-5 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold ml-2">{freePlan.name}</h3>
                    </div>
                    {userData?.userPlan === 'free' && (
                      <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                        {t('upgrade.current') || 'Current'}
                      </span>
                    )}
                  </div>

                  <div className="mb-3">
                    <span className="text-2xl font-bold">€{freePlan.pricing.monthly.amount}</span>
                    <span className="text-gray-600 text-sm">{t('common.perMonth') || '/month'}</span>
                  </div>

                  <ul className="space-y-2 mb-3">
                    {getFeaturesToShow(freePlan, 'free').map((feature, index) => (
                      <li key={index} className="flex items-center">
                        {feature.included ? (
                          <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-red-400 mr-2 flex-shrink-0" />
                        )}
                        <span className={`text-xs ${feature.included ? "text-gray-700" : "text-gray-400"}`}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {freePlan.features?.length > 5 && (
                    <button
                      onClick={() => toggleFeatures('free')}
                      className="flex items-center justify-center w-full py-2 text-xs text-[#253985] font-medium mb-3"
                    >
                      {expandedFeatures.free ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          {t('upgrade.hide') || 'See Less'}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          {t('upgrade.seeMore') || `See ${freePlan.features.length - 5} More`}
                        </>
                      )}
                    </button>
                  )}

                  <button
                    className={`w-full py-2 rounded-lg font-medium text-sm ${
                      userData?.userPlan === 'free' ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-800'
                    }`}
                    disabled={userData?.userPlan === 'free'}
                  >
                    {userData?.userPlan === 'free'
                      ? t('upgrade.currentPlan') || 'Current Plan'
                      : t('upgrade.selectFreePlan') || 'Select Free Plan'}
                  </button>
                </div>
              )}

              {/* Premium Plan */}
              {premiumPlan && (
                <div
                  className={`border-2 rounded-xl p-4 transition-all relative ${
                    userData?.userPlan === 'premium' ? 'border-[#253985] shadow-lg bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  {premiumPlan.metadata?.recommended && (
                    <div className="absolute top-0 right-0 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-bl-lg rounded-tr-lg">
                      {t('upgrade.recommended') || 'RECOMMENDED'}
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="bg-purple-100 p-1 rounded-lg">
                        <Crown className="h-5 w-5 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold ml-2">{premiumPlan.name}</h3>
                    </div>
                    {userData?.userPlan === 'premium' && (
                      <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                        {t('upgrade.current') || 'Current'}
                      </span>
                    )}
                  </div>

                  {/* Price Display – changes based on billing interval */}
                  <div className="mb-3">
                    {billingInterval === 'monthly' ? (
                      <>
                        <span className="text-2xl font-bold">€{premiumPlan.pricing.monthly.amount}</span>
                        <span className="text-gray-600 text-sm">{t('common.perMonth') || '/month'}</span>
                        {premiumPlan.pricing.yearly && (
                          <p className="text-xs text-gray-500 mt-1">
                            {t('upgrade.orYearly') || 'or'} €{premiumPlan.pricing.yearly.amount}
                            {t('upgrade.perYear') || '/year'} 
                            {(() => {
                              const monthly = premiumPlan.pricing.monthly.amount;
                              const yearly = premiumPlan.pricing.yearly.amount;
                              const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                              return discount > 0 ? ` (save ${discount}%)` : '';
                            })()}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="text-2xl font-bold">€{premiumPlan.pricing.yearly.amount}</span>
                        <span className="text-gray-600 text-sm">{t('upgrade.perYear') || '/year'}</span>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('upgrade.just') || 'just'} €{(premiumPlan.pricing.yearly.amount / 12).toFixed(2)}
                          {t('common.perMonth') || '/month'} {t('upgrade.whenBilledAnnually') || 'when billed annually'}
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="space-y-2 mb-3">
                    {getFeaturesToShow(premiumPlan, 'premium').map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-xs text-gray-700">{feature.name}</span>
                      </li>
                    ))}
                  </ul>

                  {premiumPlan.features?.length > 5 && (
                    <button
                      onClick={() => toggleFeatures('premium')}
                      className="flex items-center justify-center w-full py-2 text-xs text-[#253985] font-medium mb-3"
                    >
                      {expandedFeatures.premium ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          {t('upgrade.hide') || 'See Less'}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          {t('upgrade.seeMore') || `See ${premiumPlan.features.length - 5} More`}
                        </>
                      )}
                    </button>
                  )}

                  {/* Billing Interval Toggle – with floating savings badge */}
                  {premiumPlan.pricing.yearly && (
                    <div className="relative mb-3">
                      {/* Discount badge placed above the yearly side */}
                      {(() => {
                        const monthly = premiumPlan.pricing.monthly.amount;
                        const yearly = premiumPlan.pricing.yearly.amount;
                        const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                        if (discount > 0) {
                          return (
                            <div className="absolute -top-2 right-0 left-1/2 transform -translate-x-1/2 w-max z-10">
                              <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                Save {discount}%
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* The toggle itself */}
                      <div className="flex bg-gray-100 rounded-lg p-1 relative">
                        <button
                          className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                            billingInterval === 'monthly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                          }`}
                          onClick={() => setBillingInterval('monthly')}
                        >
                          {t('upgrade.monthly') || 'Monthly'} · €{premiumPlan.pricing.monthly.amount}/mo
                        </button>
                        <button
                          className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                            billingInterval === 'yearly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                          }`}
                          onClick={() => setBillingInterval('yearly')}
                        >
                          {t('upgrade.yearly') || 'Yearly'} · €{premiumPlan.pricing.yearly.amount}/year
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Platform‑specific Purchase Button */}
                  {isIOS ? (
                    <button
                      className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center ${
                        userData?.userPlan === 'premium'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : !isUpgradeAllowed()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : processingIAP
                          ? 'bg-[#1a2c6d] text-white cursor-not-allowed'
                          : 'bg-[#253985] text-white'
                      }`}
                      disabled={userData?.userPlan === 'premium' || !isUpgradeAllowed() || processingIAP}
                      onClick={() => handleIosPurchase(premiumPlan)}
                    >
                      {processingIAP ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {t('upgrade.processing') || 'Processing...'}
                        </>
                      ) : userData?.userPlan === 'premium' ? (
                        t('upgrade.currentPlan') || 'Current Plan'
                      ) : !isUpgradeAllowed() ? (
                        userData.userType === 'individual'
                          ? t('upgrade.availableForPersonalOnly') || 'Available for Personal accounts only'
                          : t('upgrade.availableForBusinessOnly') || 'Available for Business accounts only'
                      ) : userType === 'company' ? (
                        t('upgrade.upgradeToBusiness') || 'Upgrade to Business'
                      ) : (
                        t('upgrade.upgradeToPremium') || 'Upgrade to Premium'
                      )}
                    </button>
                  ) : (
                    <button
                      className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center ${
                        userData?.userPlan === 'premium'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : !isUpgradeAllowed()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : processingPayment
                          ? 'bg-[#1a2c6d] text-white cursor-not-allowed'
                          : 'bg-[#253985] text-white'
                      }`}
                      disabled={userData?.userPlan === 'premium' || !isUpgradeAllowed() || processingPayment}
                      onClick={() => handleUpgrade(premiumPlan)}
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {t('upgrade.processing') || 'Processing...'}
                        </>
                      ) : userData?.userPlan === 'premium' ? (
                        t('upgrade.currentPlan') || 'Current Plan'
                      ) : !isUpgradeAllowed() ? (
                        userData.userType === 'individual'
                          ? t('upgrade.availableForPersonalOnly') || 'Available for Personal accounts only'
                          : t('upgrade.availableForBusinessOnly') || 'Available for Business accounts only'
                      ) : userType === 'company' ? (
                        t('upgrade.upgradeToBusiness') || 'Upgrade to Business'
                      ) : (
                        t('upgrade.upgradeToPremium') || 'Upgrade to Premium'
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Support Section - Hidden on iOS */}
            {!isIOS && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex flex-col items-center justify-between">
                  <div className="mb-3 text-center">
                    <h3 className="text-sm font-semibold text-gray-800">
                      {t('upgrade.needChangeAccountType') || 'Need to change your account type?'}
                    </h3>
                    <p className="text-gray-600 text-xs">
                      {t('upgrade.contactSupportDescription') || 'Contact our support team to change your account type'}
                    </p>
                  </div>
                  <button
                    onClick={() => alert(t('upgrade.contactSupportMessage') || "Contact support at support@easysubs.it to change your account type")}
                    className="px-4 py-2 bg-[#253985] text-white rounded-lg text-sm whitespace-nowrap"
                  >
                    {t('upgrade.contactSupport') || 'Contact Support'}
                  </button>
                </div>
              </div>
            )}

            {/* Privacy Policy and Terms Links */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-center space-x-4 text-xs text-gray-500">
                <a
                  href="https://easysubs.it/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#253985] transition-colors"
                >
                  {t('footer.termsOfUse') || 'Terms of Use'}
                </a>
                <span>•</span>
                <a
                  href="https://easysubs.it/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#253985] transition-colors"
                >
                  {t('footer.privacyPolicy') || 'Privacy Policy'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav
        navItems={navItems}
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileUpgrade;